import { Save, Trash2 } from "lucide-react";
import type { FC, KeyboardEvent } from "react";

import { m } from "@recobra/i18n/messages";
import type { Task, TaskReminder } from "@recobra/schemas/task";
import { Button } from "@recobra/ui/button";
import { ButtonGroup } from "@recobra/ui/button-group";
import { DatePicker } from "@recobra/ui/custom/date-picker";
import { Field, FieldGroup } from "@recobra/ui/field";
import { Input } from "@recobra/ui/input";

import { useTaskReminderDraft } from "./useTaskReminderDraft";

export const TaskReminderRow: FC<{
  task: Task;
  reminder: TaskReminder;
  isNew: boolean;
  canUpdate: boolean;
  canRemove: boolean;
  onDiscardNew: () => void;
}> = ({ task, reminder, isNew, canUpdate, canRemove, onDiscardNew }) => {
  const draft = useTaskReminderDraft({ task, reminder, isNew, onDiscardNew });
  const editable = isNew || (canUpdate && reminder.status === "pending");
  const locked = !editable || draft.isPending;
  const saveOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    draft.save();
  };

  return (
    <li>
      <FieldGroup>
        <Field orientation="responsive">
          <DatePicker
            value={draft.scheduledDate}
            onChange={(value) => draft.setScheduledDate(value ?? "")}
            minDate={draft.earliestDate}
            aria-label={m.task_reminders_scheduled_date()}
            disabled={locked}
            required
          />
          <Input
            value={draft.comment}
            maxLength={2000}
            placeholder={m.task_reminders_comment_placeholder()}
            aria-label={m.task_reminders_comment()}
            disabled={locked}
            onChange={(event) => draft.setComment(event.target.value)}
            onKeyDown={saveOnEnter}
          />
          <ButtonGroup>
            <Button
              type="button"
              size="icon-xs"
              aria-label={m.task_reminders_save()}
              disabled={locked || !draft.isStorable}
              onClick={draft.save}
            >
              <Save />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={m.task_reminders_remove()}
              disabled={!(isNew || canRemove) || draft.isPending}
              onClick={draft.discard}
            >
              <Trash2 />
            </Button>
          </ButtonGroup>
        </Field>
      </FieldGroup>
    </li>
  );
};
